import React, { useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  useColorModeValue,
  Flex,
  Icon,
  IconButton,
  Tooltip,
  useToast,
  VStack,
  Divider,
  Button,
} from "@chakra-ui/react";
import { MdBolt, MdLocationOn, MdVisibility } from "react-icons/md";
import { IoStopCircleOutline } from "react-icons/io5";
import useAxios from "axios-hooks";
import { useNavigate } from "react-router-dom";

const OngoingSessionsTable = ({ sessions, onRefresh }) => {
  const [stoppingSessionId, setStoppingSessionId] = useState(null);
  const navigate = useNavigate();
  const toast = useToast();
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const labelColor = useColorModeValue("gray.600", "gray.400");

  const [{ loading: stopLoading }, executeStopSession] = useAxios(
    { method: "POST" },
    { manual: true }
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const stopSessionHandler = async (session) => {
    setStoppingSessionId(session.Id);
    var payload = {};
    payload["StationId"] = session.EvseId;
    payload["TransactionId"] = session.Id;
    try {
      const response = await executeStopSession({
        method: "POST",
        url: `/api/ocpp16/charging/stop`,
        data: payload,
      });
      if (response.status === 200) {
        toast({
          title: "Session Stopped",
          description: "Charging session stopped successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (error) {
      toast({
        title: "Error Stopping Session",
        description: error.response?.data?.error || "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setStoppingSessionId(null);
    }
  };

  if (!sessions || sessions.length === 0) {
    return (
      <Box bg={cardBg} p={8} borderRadius="lg" textAlign="center" border="1px solid" borderColor={borderColor}>
        <Icon as={MdBolt} boxSize={12} color="gray.300" mb={3} />
        <Text color={labelColor}>No active sessions</Text>
      </Box>
    );
  }

  return (
    <Box
      bg={cardBg}
      borderRadius="lg"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      boxShadow="sm"
    >
      {/* Desktop Table View */}
      <Box display={{ base: "none", lg: "block" }} overflowX="auto">
        <Table variant="simple">
          <Thead bg="rgba(245, 125, 54, 0.05)">
            <Tr>
              <Th color={labelColor} fontSize="xs" textTransform="uppercase">Session ID</Th>
              <Th color={labelColor} fontSize="xs" textTransform="uppercase">Mobile Number</Th>
              <Th color={labelColor} fontSize="xs" textTransform="uppercase">Start Time</Th>
              <Th color={labelColor} fontSize="xs" textTransform="uppercase">Station</Th>
              <Th color={labelColor} fontSize="xs" textTransform="uppercase">Energy (kWh)</Th>
              <Th color={labelColor} fontSize="xs" textTransform="uppercase">Cost (₹)</Th>
              <Th color={labelColor} fontSize="xs" textTransform="uppercase">Status</Th>
              <Th color={labelColor} fontSize="xs" textTransform="uppercase">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {sessions.map((session, index) => (
              <Tr
                key={session.Id || index}
                _hover={{ bg: "rgba(245, 125, 54, 0.02)" }}
                transition="all 0.2s"
              >
                <Td>
                  <Text fontSize="sm" fontWeight="medium" color={textColor}>
                    {session?.Id}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="sm" fontWeight="medium" color={textColor}>
                    {session?.MobileNumber || session?.IdTag || "N/A"}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="sm" color={labelColor}>
                    {formatDate(session?.StartTime)}
                  </Text>
                </Td>
                <Td>
                  <Flex align="center" gap={2}>
                    <Icon as={MdLocationOn} color="#f57d36" boxSize={4} />
                    <Text fontSize="sm" color={textColor}>
                      {session?.StationName || session?.EvseId || "N/A"}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Text fontSize="sm" fontWeight="medium" color={textColor}>
                    {session.UnitsConsumed?.toFixed(2) || "0.00"}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="sm" fontWeight="bold" color="#f57d36">
                    ₹{session.TotalCostWithTax?.toFixed(2) || "0.00"}
                  </Text>
                </Td>
                <Td>
                  <Badge
                    colorScheme="orange"
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="md"
                  >
                    {session?.Status || "ONGOING"}
                  </Badge>
                </Td>
                <Td>
                  <Flex justify="center" gap={2}>
                    <Tooltip label="View Details" hasArrow>
                      <IconButton
                        colorScheme="orange"
                        variant="ghost"
                        size="sm"
                        borderRadius="full"
                        aria-label="View"
                        _hover={{
                          bg: "orange.50",
                          transform: "scale(1.1)",
                          transition: "all 0.2s",
                        }}
                        icon={<Icon as={MdVisibility} boxSize={5} />}
                        onClick={() => navigate(`/admin/charging_management/session/${session.Id}`)}
                      />
                    </Tooltip>
                    <Tooltip label="Stop Charging" hasArrow>
                      <IconButton
                        colorScheme="red"
                        variant="ghost"
                        size="sm"
                        borderRadius="full"
                        aria-label="Stop"
                        isLoading={stoppingSessionId === session?.Id}
                        _hover={{
                          bg: "red.50",
                          transform: "scale(1.1)",
                          transition: "all 0.2s",
                        }}
                        icon={<Icon as={IoStopCircleOutline} boxSize={5} />}
                        onClick={() => stopSessionHandler(session)}
                      />
                    </Tooltip>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Mobile Card View */}
      <VStack display={{ base: "flex", lg: "none" }} spacing={2} p={2} align="stretch">
        {sessions.map((session, index) => (
          <Box
            key={session.Id || index}
            bg={cardBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="md"
            p={2}
            _hover={{ borderColor: "#f57d36", boxShadow: "md" }}
            transition="all 0.2s"
          >
            {/* Header: Session ID and Status */}
            <Flex justify="space-between" align="center" mb={2}>
              <Text fontSize="sm" fontWeight="bold" color={textColor}>
                #{session?.Id}
              </Text>
              <Badge
                colorScheme="orange"
                fontSize="2xs"
                px={1.5}
                py={0.5}
                borderRadius="md"
              >
                {session?.Status || "ONGOING"}
              </Badge>
            </Flex>

            <Divider mb={2} />

            {/* Mobile Number */}
            <Box
              bg="rgba(245, 125, 54, 0.05)"
              border="1px solid"
              borderColor="rgba(245, 125, 54, 0.2)"
              borderRadius="md"
              p={1.5}
              mb={1.5}
            >
              <VStack align="start" spacing={0}>
                <Text fontSize="2xs" color={labelColor} fontWeight="semibold">
                  MOBILE NUMBER
                </Text>
                <Text fontSize="xs" fontWeight="medium" color={textColor}>
                  {session?.MobileNumber || session?.IdTag || "N/A"}
                </Text>
              </VStack>
            </Box>

            {/* Start Time */}
            <Box
              bg="rgba(245, 125, 54, 0.05)"
              border="1px solid"
              borderColor="rgba(245, 125, 54, 0.2)"
              borderRadius="md"
              p={1.5}
              mb={1.5}
            >
              <VStack align="start" spacing={0}>
                <Text fontSize="2xs" color={labelColor} fontWeight="semibold">
                  START TIME
                </Text>
                <Text fontSize="xs" fontWeight="medium" color={textColor}>
                  {formatDate(session?.StartTime)}
                </Text>
              </VStack>
            </Box>

            {/* Station, Energy, Cost in 3 columns */}
            <Flex gap={1.5} mb={1.5}>
              <Box
                bg="rgba(245, 125, 54, 0.05)"
                border="1px solid"
                borderColor="rgba(245, 125, 54, 0.2)"
                borderRadius="md"
                p={1.5}
                flex={1}
              >
                <Flex align="center" gap={1}>
                  <Icon as={MdLocationOn} color="#f57d36" boxSize={3} />
                  <VStack align="start" spacing={0} flex={1}>
                    <Text fontSize="2xs" color={labelColor} fontWeight="semibold">
                      STATION
                    </Text>
                    <Text fontSize="xs" fontWeight="bold" color={textColor}>
                      {session?.StationName || session?.EvseId || "N/A"}
                    </Text>
                  </VStack>
                </Flex>
              </Box>

              <Box
                bg="rgba(245, 125, 54, 0.05)"
                border="1px solid"
                borderColor="rgba(245, 125, 54, 0.2)"
                borderRadius="md"
                p={1.5}
                flex={1}
              >
                <VStack align="start" spacing={0}>
                  <Text fontSize="2xs" color={labelColor} fontWeight="semibold">
                    ENERGY
                  </Text>
                  <Text fontSize="xs" fontWeight="bold" color={textColor}>
                    {session?.UnitsConsumed?.toFixed(2) || "0.00"}
                  </Text>
                </VStack>
              </Box>

              <Box
                bg="rgba(245, 125, 54, 0.05)"
                border="1px solid"
                borderColor="rgba(245, 125, 54, 0.2)"
                borderRadius="md"
                p={1.5}
                flex={1}
              >
                <VStack align="start" spacing={0}>
                  <Text fontSize="2xs" color={labelColor} fontWeight="semibold">
                    COST
                  </Text>
                  <Text fontSize="xs" fontWeight="bold" color="#f57d36">
                    ₹{session?.TotalCostWithTax?.toFixed(2) || "0.00"}
                  </Text>
                </VStack>
              </Box>
            </Flex>

            {/* Action Buttons */}
            <Flex gap={2}>
              <Button
                flex={1}
                size="sm"
                colorScheme="orange"
                variant="outline"
                borderRadius="md"
                leftIcon={<Icon as={MdVisibility} />}
                onClick={() => navigate(`/admin/charging_management/session/${session.Id}`)}
                fontSize="xs"
                h="32px"
              >
                View
              </Button>
              <Button
                flex={1}
                size="sm"
                colorScheme="red"
                variant="outline"
                borderRadius="md"
                leftIcon={<Icon as={IoStopCircleOutline} />}
                onClick={() => stopSessionHandler(session)}
                isLoading={stoppingSessionId === session.Id}
                fontSize="xs"
                h="32px"
              >
                Stop
              </Button>
            </Flex>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

export default OngoingSessionsTable;
